/**
 * Reactive context for tracking dependencies and coordinating flush cycles for
 * reactive observers and render-side patch work.
 *
 * The design intentionally mirrors the small-runtime ideas used by Solid and
 * Svelte: stable observer queues, dirty invalidation, and one flush per local
 * context instead of chained async hops.
 *
 * @module @smoothie-framework/ui/state
 */
import { createFlushContext, defaultFlushContext } from './flush-context';
import { getOwner } from './scope';
import { reportRuntimeError } from './diagnostics';
const REACTIVE_CONTEXT_KEY = Symbol.for('@smoothie-framework/ui/reactive-context');
const reactiveGlobal = globalThis;
export const context = reactiveGlobal[REACTIVE_CONTEXT_KEY] ?? {
    currentObserver: null,
    batchDepth: 0,
    pendingUpdates: defaultFlushContext.pendingUpdates,
};
reactiveGlobal[REACTIVE_CONTEXT_KEY] = context;
const activeFlushContexts = new Set([defaultFlushContext]);
export { defaultFlushContext };
export { createFlushContext };
export function getCurrentObserver() {
    return context.currentObserver;
}
export function setCurrentObserver(observer) {
    context.currentObserver = observer;
}
export function isInBatch() {
    return context.batchDepth > 0;
}
export function requestFlush(schedule = queueMicrotask, flushContext = getCurrentFlushContext()) {
    if (flushContext.disposed || flushContext.flushScheduled || flushContext.isFlushing) {
        return;
    }
    flushContext.flushScheduled = true;
    activeFlushContexts.add(flushContext);
    resolveFlushScheduler(schedule, flushContext)(() => {
        flushUpdates(flushContext);
    });
}
export function registerFlushListener(listener, flushContext = getCurrentFlushContext()) {
    flushContext.flushListeners.add(listener);
    activeFlushContexts.add(flushContext);
    return () => {
        flushContext.flushListeners.delete(listener);
    };
}
export function schedulePostCommitTask(task, flushContext = getCurrentFlushContext()) {
    flushContext.postCommitTasks.add(task);
    activeFlushContexts.add(flushContext);
    if (!isInBatch() && !flushContext.flushScheduled && !flushContext.isFlushing) {
        requestFlush(queueMicrotask, flushContext);
    }
}
export function scheduleUpdate(observer) {
    observer.invalidate?.();
    const flushContext = observer.flushContext;
    flushContext.pendingUpdates.add(observer);
    activeFlushContexts.add(flushContext);
    if (!isInBatch() && !flushContext.flushScheduled && !flushContext.isFlushing) {
        requestFlush(queueMicrotask, flushContext);
    }
}
export function flushUpdates(flushContext) {
    if (flushContext) {
        flushContextUpdates(flushContext);
        return;
    }
    const maxIterations = 100;
    let iterations = 0;
    while (iterations < maxIterations) {
        const pendingContexts = Array.from(activeFlushContexts).filter(hasPendingFlushWork);
        if (pendingContexts.length === 0) {
            return;
        }
        for (const pendingFlushContext of pendingContexts) {
            flushContextUpdates(pendingFlushContext);
        }
        iterations += 1;
    }
    for (const pendingFlushContext of Array.from(activeFlushContexts)) {
        pendingFlushContext.pendingUpdates.clear();
        pendingFlushContext.postCommitTasks.clear();
        pendingFlushContext.flushScheduled = false;
        pendingFlushContext.isFlushing = false;
    }
    reportRuntimeError('reactive-loop', new Error('Exceeded max reactive update iterations'));
}
export function disposeFlushContext(flushContext) {
    if (flushContext === defaultFlushContext || flushContext.disposed) {
        return;
    }
    flushContext.disposed = true;
    flushContext.pendingUpdates.clear();
    flushContext.flushListeners.clear();
    flushContext.postCommitTasks.clear();
    flushContext.flushScheduled = false;
    flushContext.isFlushing = false;
    activeFlushContexts.delete(flushContext);
}
function flushContextUpdates(flushContext) {
    if (flushContext.disposed || flushContext.isFlushing) {
        return;
    }
    flushContext.flushScheduled = false;
    flushContext.isFlushing = true;
    const maxIterations = 100;
    let iterations = 0;
    try {
        while (iterations < maxIterations) {
            if (flushContext.pendingUpdates.size > 0) {
                const updates = Array.from(flushContext.pendingUpdates);
                flushContext.pendingUpdates.clear();
                for (const observer of updates) {
                    try {
                        observer.notify();
                    }
                    catch (error) {
                        reportRuntimeError('observer', error);
                    }
                }
            }
            const listeners = Array.from(flushContext.flushListeners);
            for (const listener of listeners) {
                try {
                    listener();
                }
                catch (error) {
                    reportRuntimeError('flush-listener', error);
                }
            }
            const tasks = Array.from(flushContext.postCommitTasks);
            flushContext.postCommitTasks.clear();
            for (const task of tasks) {
                try {
                    task();
                }
                catch (error) {
                    reportRuntimeError('flush-listener', error);
                }
            }
            iterations += 1;
            if (flushContext.pendingUpdates.size === 0 && flushContext.postCommitTasks.size === 0) {
                break;
            }
        }
    }
    finally {
        flushContext.isFlushing = false;
        flushContext.flushScheduled = false;
    }
    if (flushContext.pendingUpdates.size > 0 || iterations >= maxIterations) {
        flushContext.pendingUpdates.clear();
        flushContext.postCommitTasks.clear();
        reportRuntimeError('reactive-loop', new Error('Exceeded max reactive update iterations'));
    }
}
function hasPendingFlushWork(flushContext) {
    return !flushContext.disposed && !flushContext.isFlushing && (flushContext.pendingUpdates.size > 0
        || flushContext.postCommitTasks.size > 0
        || flushContext.flushScheduled);
}
function getCurrentFlushContext() {
    return getOwner()?.flushContext ?? defaultFlushContext;
}
function resolveFlushScheduler(explicitSchedule, flushContext) {
    if (explicitSchedule !== queueMicrotask) {
        return explicitSchedule;
    }
    return flushContext.scheduler;
}
//# sourceMappingURL=context.js.map